import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Globe, Shield, UserCheck, Lock, ArrowRight, Award } from 'lucide-react';
import { useParallax, staggerContainer, staggerItem, slideLeft, slideRight, useCounter } from '../hooks/useAnimations';
import LazyImage from '../components/LazyImage';

const INTERIOR_IMG = 'https://static.prod-images.emergentagent.com/jobs/7621458b-ca63-4125-ad1b-115cf548682c/images/ca7cf37d3af34a14ef9535c7ef238bf0f220cc3b1fb9d3f8b6f6a609b29b6d56.png';
const TRAVEL_IMG = 'https://static.prod-images.emergentagent.com/jobs/7621458b-ca63-4125-ad1b-115cf548682c/images/474fcaae205b7391782b6f63001d9cad475a62c831d1dc12ea6e58f1abe5cbc8.png';

function ParallaxHeroImage({ src, alt }) {
  const { ref, y } = useParallax(60);
  return (
    <div ref={ref} className="aspect-[4/3] overflow-hidden bg-[#0A120E]">
      <motion.img src={src} alt={alt} style={{ y }} className="w-full h-[130%] object-cover" />
    </div>
  );
}

function CounterStat({ end, suffix = '', label }) {
  const { ref, count } = useCounter(end);
  return (
    <div ref={ref} className="text-center p-6 border border-white/5">
      <p className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{count}{suffix}</p>
      <p className="text-sm text-zinc-500 mt-2">{label}</p>
    </div>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#050505] pt-20" data-testid="about-page">
      {/* Hero - Split with text reveal + parallax image */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={staggerContainer}>
              <motion.p variants={staggerItem} className="overline mb-4">Nuestra Filosofia</motion.p>
              <motion.h1 className="text-5xl md:text-6xl font-bold tracking-tighter text-white leading-[0.95] overflow-hidden" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                <motion.span className="inline-block" initial={{ y: 80 }} whileInView={{ y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}>
                  No somos una agencia.
                </motion.span>
                <br />
                <motion.span className="inline-block" initial={{ y: 80 }} whileInView={{ y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}>
                  Somos tu concierge
                </motion.span>
                <br />
                <motion.span className="inline-block" initial={{ y: 80 }} whileInView={{ y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}>
                  de golf privado.
                </motion.span>
              </motion.h1>
              <motion.p variants={staggerItem} className="text-base md:text-lg text-zinc-400 mt-6 leading-relaxed max-w-lg">
                Elite Golf MX existe para crear las experiencias de golf mas extraordinarias del mundo.
                No vendemos paquetes. Disenamos momentos irrepetibles, personalizados con precision absoluta.
              </motion.p>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 60 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}>
              <ParallaxHeroImage src={INTERIOR_IMG} alt="Elite Golf MX" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Counter */}
      <section className="py-16 bg-[#0A120E] border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-3 gap-4 md:gap-8">
            <CounterStat end={30} suffix="+" label="Paises Cubiertos" />
            <CounterStat end={500} suffix="+" label="Experiencias Creadas" />
            <CounterStat end={100} suffix="%" label="Satisfaccion" />
          </div>
        </div>
      </section>

      {/* Philosophy - Staggered cards */}
      <section className="py-24 md:py-32 bg-[#0A120E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
            <motion.p variants={staggerItem} className="overline mb-3">Nuestra Promesa</motion.p>
            <motion.h2 variants={staggerItem} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Excelencia en Cada Detalle
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Globe, title: 'Alcance Global', desc: 'Red de contactos en los campos mas exclusivos de cada continente. Acceso que otros simplemente no tienen.' },
              { icon: Shield, title: 'Acceso Privilegiado', desc: 'Horarios privados, tee times reservados y acceso a clubes members-only en todo el mundo.' },
              { icon: UserCheck, title: 'Personalizacion Total', desc: 'Cada experiencia se disena desde cero. No hay dos viajes iguales. Tu vision, nuestra ejecucion.' },
              { icon: Lock, title: 'Discrecion Absoluta', desc: 'Privacidad y confidencialidad en cada interaccion. Servicio white-glove sin compromisos.' },
            ].map((item) => (
              <motion.div key={item.title} variants={staggerItem}
                className="p-8 border border-white/5 hover:border-white/15 transition-all duration-500 group">
                <item.icon className="w-7 h-7 text-zinc-500 mb-6 transition-transform duration-500 group-hover:scale-110" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{item.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Manifesto with parallax background */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <LazyImage src={TRAVEL_IMG} alt="" className="!absolute inset-0 opacity-15" aspectClass="w-full h-full" />
        <div className="absolute inset-0 bg-[#050505]/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={staggerContainer}>
            <motion.div variants={staggerItem}><Award className="w-8 h-8 text-zinc-600 mx-auto mb-6" strokeWidth={1.5} /></motion.div>
            <motion.h2 variants={staggerItem} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              El concierge de golf mas exclusivo de Latinoamerica.
            </motion.h2>
            <motion.p variants={staggerItem} className="text-base md:text-lg text-zinc-400 mt-8 max-w-2xl mx-auto leading-relaxed">
              Conectamos a los golfistas mas exigentes del mundo con experiencias que trascienden lo ordinario.
              Cada campo, cada detalle, cada momento — disenado con la precision de un swing perfecto.
            </motion.p>
            <motion.div variants={staggerItem} className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Link to="/contacto" className="bg-white text-black px-8 py-4 text-sm font-medium hover:bg-zinc-200 transition-colors inline-flex items-center gap-2 group" data-testid="about-cta">
                Planear Mi Experiencia <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
